import { useEffect, useState } from "react";
import API from "../lib/api";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Drop-in client picker identical to the one on Jobs.
 * Controls the client_*, requester_contact_* fields on any form.
 *
 * Props:
 *   - value: the current form object (must expose client_id, client_name, client_email, client_phone,
 *            client_company, requester_contact_id, requester_contact_name, requester_contact_color)
 *   - onChange(nextForm) — called with the merged next form
 *   - testIdPrefix (default "client") — used for data-testid attributes
 */
export default function ClientPicker({ value, onChange, testIdPrefix = "client", manualFields = true }) {
  const [clients, setClients] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);

  useEffect(() => {
    API.get("/clients?limit=500")
      .then(({ data }) => setClients(data.clients || []))
      .catch(() => {});
  }, []);

  // Keep sub-contacts list in sync when the form's client_id changes externally
  // (e.g. edit dialog populated from an existing project).
  useEffect(() => {
    if (!value?.client_id) {
      setSelectedContacts([]);
      return;
    }
    const c = clients.find(c => c.id === value.client_id);
    setSelectedContacts(c?.contacts || []);
  }, [value?.client_id, clients]);

  const pickClient = (clientId) => {
    if (!clientId || clientId === "__none__") {
      onChange({
        ...value,
        client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "",
        requester_contact_id: "", requester_contact_name: "", requester_contact_color: "",
      });
      setSelectedContacts([]);
      return;
    }
    const c = clients.find(c => c.id === clientId);
    if (!c) return;
    onChange({
      ...value,
      client_id: c.id,
      client_name: `${c.first_name} ${c.last_name}`.trim(),
      client_email: c.email || "",
      client_phone: c.phone || "",
      client_company: c.company_name || "",
      requester_contact_id: "",
      requester_contact_name: "",
      requester_contact_color: "",
    });
    setSelectedContacts(c.contacts || []);
  };

  const pickRequester = (contactId) => {
    if (!contactId || contactId === "__none__") {
      onChange({ ...value, requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" });
      return;
    }
    const contact = selectedContacts.find(c => c.id === contactId);
    if (!contact) return;
    onChange({
      ...value,
      requester_contact_id: contact.id,
      requester_contact_name: `${contact.first_name} ${contact.last_name}`.trim(),
      requester_contact_color: contact.color || "#3b82f6",
    });
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-picker`}>
      <div>
        <Label>Client</Label>
        <Select value={value?.client_id || "__none__"} onValueChange={pickClient}>
          <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-select`}>
            <SelectValue placeholder="Select a client..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No client selected</SelectItem>
            {clients.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.company_name ? `${c.company_name} — ${c.first_name} ${c.last_name}` : `${c.first_name} ${c.last_name}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {value?.client_id && selectedContacts.length > 0 && (
        <div>
          <Label>Requester Contact</Label>
          <Select value={value?.requester_contact_id || "__none__"} onValueChange={pickRequester}>
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-requester-select`}>
              <SelectValue placeholder="Tag a sub-contact as requester..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No specific requester</SelectItem>
              {selectedContacts.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0 inline-block" style={{ backgroundColor: c.color || "#3b82f6" }} />
                    {c.first_name} {c.last_name}{c.job_role ? ` (${c.job_role})` : ""}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {value?.client_id && (
        <div className="grid grid-cols-2 gap-3 border border-border rounded-md p-3 bg-muted/20" data-testid={`${testIdPrefix}-summary`}>
          <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Name</Label><p className="text-sm font-medium mt-0.5">{value?.client_name || "—"}</p></div>
          <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Company</Label><p className="text-sm font-medium mt-0.5">{value?.client_company || "—"}</p></div>
          <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</Label><p className="text-sm font-medium mt-0.5">{value?.client_email || "—"}</p></div>
          <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone</Label><p className="text-sm font-medium mt-0.5">{value?.client_phone || "—"}</p></div>
          {value?.requester_contact_name && (
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Requester</Label>
              <span
                className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: value?.requester_contact_color || "#3b82f6" }}
                data-testid={`${testIdPrefix}-requester-tag`}
              >
                {value?.requester_contact_name}
              </span>
            </div>
          )}
        </div>
      )}

      {!value?.client_id && manualFields && (
        <div className="grid grid-cols-2 gap-3" data-testid={`${testIdPrefix}-manual-fields`}>
          <div><Label>Name</Label><Input value={value?.client_name || ""} onChange={e => onChange({ ...value, client_name: e.target.value })} className="mt-1" placeholder="John Smith" data-testid={`${testIdPrefix}-manual-name`} /></div>
          <div><Label>Company</Label><Input value={value?.client_company || ""} onChange={e => onChange({ ...value, client_company: e.target.value })} className="mt-1" placeholder="Smith Properties Ltd" /></div>
          <div><Label>Email</Label><Input type="email" value={value?.client_email || ""} onChange={e => onChange({ ...value, client_email: e.target.value })} className="mt-1" placeholder="john@smith.com" /></div>
          <div><Label>Phone</Label><Input value={value?.client_phone || ""} onChange={e => onChange({ ...value, client_phone: e.target.value })} className="mt-1" placeholder="+44 7..." /></div>
        </div>
      )}
    </div>
  );
}
